<template>
  <div :class="$attrs.class" :style="$attrs.style as any">
    <label v-if="label" class="form-label" :class="{ required: isRequired }">{{ label }}</label>

    <div class="row">
      <div class="form-input col">
        <div v-if="type === 'switch'">
          <label class="form-check form-switch">
            <input
              v-bind="controlAttrs"
              type="checkbox"
              class="form-check-input"
              :class="inputClass"
              :checked="model"
              @change="onSwitchChange" />
            <span class="form-check-label">{{ text }}</span>
          </label>
        </div>

        <!-- textarea is deliberately not bound to the model; call sites rely on passing :value -->
        <div v-else-if="type === 'textarea'">
          <textarea v-bind="controlAttrs" class="form-control" :class="inputClass" @input="onInput"></textarea>
        </div>

        <!-- classes read $slots directly; a computed over useSlots() misses slots added after mount -->
        <div
          v-else
          :class="{
            'input-addon': Boolean($slots.prefix || $slots.suffix),
            'lead-affix': Boolean($slots.iconStart || isRevealable || $slots.iconEnd),
          }">
          <span v-if="$slots.prefix" ref="prefixRef" class="form-input-prefix">
            <slot name="prefix" />
          </span>
          <span v-if="$slots.iconStart" class="lead-affix__icon lead-affix__icon--start">
            <slot name="iconStart" />
          </span>

          <input
            v-bind="controlAttrs"
            class="form-control"
            :class="[
              inputClass,
              {
                'lead-affix__control--start': Boolean($slots.iconStart),
                'lead-affix__control--end': Boolean(isRevealable || $slots.iconEnd),
              },
            ]"
            :type="resolvedType"
            :value="model"
            :style="affixPadding"
            @input="onInput" />

          <span v-if="$slots.suffix" ref="suffixRef" class="form-input-suffix">
            <slot name="suffix" />
          </span>
          <button
            v-if="isRevealable"
            type="button"
            class="lead-affix__icon lead-affix__icon--end lead-affix__reveal"
            :aria-label="isRevealed ? $t('form.password.hide') : $t('form.password.show')"
            @click="isRevealed = !isRevealed">
            <component :is="isRevealed ? IconEyeOff : IconEye" :size="ICON_SIZE" />
          </button>
          <span v-else-if="$slots.iconEnd" class="lead-affix__icon lead-affix__icon--end">
            <slot name="iconEnd" />
          </span>
        </div>
      </div>
    </div>

    <small v-if="hint" class="form-hint mt-2">{{ hint }}</small>

    <div v-if="$slots.hint" class="hint-text">
      <slot name="hint"></slot>
    </div>

    <Transition name="slide-fade">
      <div v-if="errorMessages.length > 0" class="error-list invalid-feedback d-block mt-1 mx-1">
        <span v-if="errorMessages.length === 1">{{ errorMessages[0] }}</span>
        <ul v-else>
          <li v-for="error in errorMessages" :key="error">{{ error }}</li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
/**
 * Standalone text input that renders its own markup rather than wrapping the component library.
 * Decorations are slots (`iconStart`, `iconEnd`, `prefix`, `suffix`, `hint`); `iconEnd` is ignored
 * while the password reveal toggle occupies its spot.
 *
 * The rendered class names are a contract: call sites pass `is-invalid` via `inputClass` and other
 * components use deep selectors on `.form-input .form-control`, `.form-input .form-input-prefix`,
 * `.form-switch .form-check-input` and `textarea.form-control`.
 */
import { computed, ref, useAttrs } from 'vue';
import { IconEye, IconEyeOff } from '@tabler/icons-vue';

interface Props {
  label?: string;
  /** label beside a switch */
  text?: string;
  inputClass?: string;
  hint?: string;
  isRequired?: boolean;
  /** one message or several; a bare string renders as a single message */
  errors?: string | string[] | null;
  /** left as a plain string: some call sites compute it at runtime */
  type?: string;
  /** show a reveal toggle on a password input; defaults on for `type="password"` */
  revealable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  // must stay undefined: Vue would cast an absent boolean to false and break the password default
  revealable: undefined,
});

const model = defineModel<any>();

defineOptions({ inheritAttrs: false });

const ICON_SIZE = 16;

/* ---------------------------------- STATE ---------------------------------- */
const attrs = useAttrs();
const prefixRef = ref<HTMLElement | null>(null);
const suffixRef = ref<HTMLElement | null>(null);
const isRevealed = ref(false);

/* -------------------------------- COMPUTED --------------------------------- */
// undefined attributes are skipped when rendering, dropping the wrapper's own class and style
const controlAttrs = computed(() => ({ ...attrs, class: undefined, style: undefined }));

const errorMessages = computed(() => {
  if (!props.errors) return [];
  return Array.isArray(props.errors) ? props.errors : [props.errors];
});

const isRevealable = computed(() => props.revealable ?? props.type === 'password');

// a number renders as text so normalise() filtering applies instead of the browser's
const resolvedType = computed(() => {
  if (props.type === 'number') return 'text';
  if (isRevealable.value && isRevealed.value) return 'text';
  return props.type;
});

// the prefix and suffix pills sit on top of the control, so it needs padding to clear them
const affixPadding = computed(() => {
  const style: Record<string, string> = {};

  if (prefixRef.value) style.paddingLeft = `${prefixRef.value.offsetWidth + 16}px`;
  if (suffixRef.value) style.paddingRight = `${suffixRef.value.offsetWidth + 16}px`;

  return style;
});

/* --------------------------------- METHODS --------------------------------- */
/** a number keeps only digits and separators, and falls back to zero when emptied */
function normalise(raw: string) {
  if (props.type !== 'number') return raw;
  return raw.replace(/[^0-9.,]/g, '') || 0;
}

function onInput(event: Event) {
  model.value = normalise((event.target as HTMLInputElement | HTMLTextAreaElement).value);
}

function onSwitchChange(event: Event) {
  model.value = (event.target as HTMLInputElement).checked;
}
</script>

<style scoped lang="scss">
.form-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
}

.hint-text {
  margin-top: 6px !important;
  font-size: 12px;
  font-weight: 400;
  color: #475467;
}

.form-input .form-control {
  padding: 8px 12px;
  border: 1px solid #d0d5dd;
  box-shadow: 0px 1px 2px 0px #1018280d;
  font-size: 16px;
  font-family: var(--font-brand);

  &:hover {
    border-color: var(--tblr-primary);
  }

  &:focus {
    box-shadow: 0px 1px 2px 0px #1018280d;
    border: 1px solid var(--tblr-primary);
    outline: 4px solid color-mix(in srgb, var(--tw-color-primary) 10%, transparent);
    outline-offset: 0px;
  }
}

.form-input .form-control::-webkit-input-placeholder {
  font-size: 16px;
  line-height: 24px;
  color: #1018284d;
  font-weight: 400;
  font-family: var(--font-brand);
}

.form-input .form-control:not(textarea) {
  height: 40px;
}

// a touch target needs to be taller than the desktop control
@media (max-width: 767.98px) {
  .form-input .form-control:not(textarea) {
    height: 44px;
  }
}

.form-switch .form-check-input {
  width: 48px;
  height: 24px;
  margin-right: 10px;
  background-size: contain;
}

.form-switch .form-check-input:checked {
  background-color: var(--tw-color-primary);
}

.input-addon,
.lead-affix {
  position: relative;
}

.form-input-prefix,
.form-input-suffix,
.lead-affix__icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  z-index: 4;
}

.form-input-prefix,
.form-input-suffix {
  padding: 0px 10px;
  font-size: 13px;
  line-height: 24px;
  color: #000000;
  text-align: center;
  white-space: nowrap;
  background-color: #e9e9e9;
  border-radius: 5px;
}

.form-input-prefix,
.lead-affix__icon--start {
  left: 12px;
}

.form-input-suffix,
.lead-affix__icon--end {
  right: 12px;
}

.lead-affix {
  &__icon {
    justify-content: center;
    color: #667085;
  }

  &__reveal {
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
      color: #344054;
    }
  }

  // clears the 16px icon plus its 12px offset; scoped under .form-input to outrank its padding rule
  .form-input &__control--start {
    padding-left: 38px;
  }

  .form-input &__control--end {
    padding-right: 38px;
  }
}

.error-list ul {
  list-style: none;
}

.error-list ul li::before {
  content: '\2022';
  color: #d63939;
  font-weight: bold;
  display: inline-block;
  width: 1em;
  margin-left: -1em;
}

.slide-fade-enter-active {
  transition:
    transform 0.3s ease-out,
    opacity 0.3s ease-out;
}

.slide-fade-leave-active {
  transition:
    transform 0.3s ease-in,
    opacity 0.3s ease-in;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}
</style>
